import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const getSavedLanguage = () => {
  try {
    const lang = window.localStorage.getItem("appLanguage");
    return lang === "ar" ? "ar" : "en";
  } catch (error) {
    return "en";
  }
};

const resources = {
  en: {
    translation: {
      resourcesHome: {
        eyebrow: "How to / Help Center",
        title: "Find the HaatSign guide your team needs",
        subtitle:
          "Search across onboarding, templates, submissions, security, and API topics, then jump into the right category in one click.",
        login: "Login",
        brandLabel: "HaatSign help center home",
        categoriesAriaLabel: "Help center categories",
        navHighlights: "Highlights",
        navCategories: "Categories",
        navQuickStart: "Quick Start",
        primaryAction: "Documentation",
        secondaryAction: "Browse Categories",
        searchLabel: "Search help center guides",
        searchPlaceholder: "Search guides, workflows, templates, or API topics",
        searchSuggestionsLabel: "Suggested guides",
        searchSuggestionsEmpty: "No guide suggestions found.",
        highlightsTitle: "Built for fast self-service help",
        highlightsSubtitle:
          "Start with the areas teams use most, then open a category card to reach the exact guide you need.",
        categoriesKicker: "Knowledge base",
        browseTitle: "Browse by category",
        browseSubtitle:
          "Each category surfaces a few key guides first, and Read more takes you to that section's first page in the sidebar.",
        searchResults:
          "{{count}} matching guides across {{sections}} categories.",
        noSearchResults: "No guides matched your search. Try a broader word.",
        clearSearch: "Clear search",
        guidesCount: "{{count}} guides",
        sectionMatches: "{{count}} matches",
        moreArticles: "+{{count}} more articles",
        readMore: "Read more",
        ctaTitle: "Get answers in minutes and keep work moving.",
        ctaSubtitle:
          "Start with Quick Start for the fastest path, or sign in to continue working inside HaatSign.",
        ctaPrimary: "Open Quick Start",
        ctaSecondary: "Sign In",
        highlightDescriptions: {
          gettingStarted:
            "Start with onboarding, core product basics, and the fastest way to send your first document.",
          templatesFields:
            "Build templates, manage fields, and configure signing workflows with fewer mistakes.",
          submissions:
            "Track document status, download outputs, and manage active or completed submissions.",
          securityCompliance:
            "Review access control, encryption, compliance topics, and signer protection guidance.",
        },
      },
      sidebar: {
        ariaLabel: "Documentation pages",
        open: "Open sidebar",
        close: "Close sidebar",
        noResults: "No matching pages found.",
      },
      common: {
        tipLabel: "Tip",
      },
      search: {
        placeholder: "Search...",
        open: "Open search",
        clear: "Clear search",
        aria: "Search documentation",
        cancel: "Cancel",
      },
      language: {
        english: "English",
        arabic: "Arabic",
        toggleLabel: "Switch language",
      },
      pdfExport: {
        buttonLabel: "Download PDF",
        downloadAction: "Download",
        preparing: "Preparing...",
        coverTitle: "HaatSign Documentation",
        coverSubtitle: "Complete guide to using the HaatSign application",
        generatedAt: "Generated on {{date}}",
        tableOfContents: "Table of Contents",
        pageNumberLabel: "Page {{current}} of {{total}}",
        errorMessage: "Unable to generate the documentation PDF.",
      },
      pageNav: {
        previous: "Previous",
        next: "Next",
      },
      footer: {
        poweredBy: "Powered by",
        haatSign: "HaatSign",
      },
      quickStart: {
        title: "Quick start",
        nav: {
          upload: "Upload",
          send: "Send",
          after: "After Sending the Document",
          certificate: "Signature Certificate",
        },
        banner:
          "New to HaatSign? This guide walks you through preparing a document, assigning recipients, and completing a secure signing workflow.",
        startTour: "Start tour",
        upload: {
          title: "Upload",
          steps: [
            "Click Upload after signing in. You can add files such as PDF or DOCX.",
            "When uploaded, the file becomes a reusable template for future requests.",
            "Use the editor to place signature, text, and date fields from the side panel.",
            "Assign each field to the correct recipient so every signer knows where to act.",
            "You can include more than one file in a workflow using Add Document.",
          ],
          note: "A clear template setup helps avoid delays and signing errors.",
          imageAlt: "template editor",
        },
        send: {
          title: "Send",
          intro:
            "After template setup, send your document to recipients for signature using the method that matches your workflow.",
          methods: [
            "Send via Email or SMS to one or more recipients with a secure signing link.",
            "Use bulk sending by uploading a CSV or XLSX list when sending at scale.",
            "Generate a shared link when you need recipients to access the document directly.",
          ],
          customize:
            "You can personalize recipient messages so instructions are clear before signing begins.",
          tip: "If you are the only signer, use Sign Yourself to complete the file immediately.",
          roles:
            "For multi-party workflows, assign roles and signing order so participants complete steps in sequence.",
          process:
            "Recipients open the document securely, complete required fields, and submit signatures without creating an account.",
          observer:
            "Observers such as CC or BCC can receive a copy after completion, keeping stakeholders informed.",
          notifications:
            "Configure who gets notified and when from recipient or submission settings.",
          signNow:
            "If you are also a signer, Sign Now lets you complete your part before forwarding to others.",
          continuation:
            "After your part is done, the workflow continues for remaining recipients in the configured order.",
          note: "A single document can include multiple signers, reviewers, and approvers in one structured flow.",
          images: {
            templateSend: "send document screen",
            recipientInfo: "recipient settings",
            recipientSubmission: "submission actions",
          },
        },
        after: {
          title: "After Sending the Document",
          p1: "Track progress from the dashboard to see who has opened, completed, or is still pending.",
          p2: "Open your sent template to view per-recipient status such as Sent, Opened, Awaiting, or Completed.",
          p3: "The Submissions tab gives a full overview with filters and search by document or signer.",
          note: "Activity logs include timestamps for sent, opened, and completed events.",
          p4: "When all required signers finish, the request is completed and final files are available.",
          p5: "For pending recipients, configure up to three reminder schedules from account settings.",
          p6: "Completed files stay available in Submissions for viewing, download, or sharing.",
          tip: "Review submissions regularly to follow up quickly and close requests on time.",
          images: {
            status: "document status overview",
            submissions: "submissions list",
            reminders: "email reminders settings",
          },
        },
        certificate: {
          title: "Signature Certificate",
          p1: "After signing is complete, the system generates a Signature Certificate that records the full signing event.",
          p2: "From the completed submission, you can open and download the certificate or audit log, separately or with the signed PDF.",
          containsTitle: "A Signature Certificate commonly contains:",
          items: [
            "Document or envelope identifier",
            "Signer emails and completed signatures",
            "Signer IP addresses and user agent details",
            "Timestamps for sent, viewed, signed, and completed events",
            "A chronological audit trail",
          ],
          note: "Download the audit log alone or together with the signed document for compliance records.",
          images: {
            certificate: "signature certificate preview",
            audit: "audit log view",
          },
        },
      },
      acceptOnlyDrawn: {
        title: "Accept only drawn signatures",
        intro:
          "Signature fields can support typed text, uploaded images, or drawn signatures. When handwriting is required, set the field to accept drawn signatures only.",
        howTo: {
          title: "How to require drawn signatures",
          steps: [
            "Open the template and select the signature field.",
            "Click the field settings icon to open field options.",
            "Choose Drawn from the signature format selector.",
            "Save the template and send your request.",
          ],
          after:
            "With this setting enabled, recipients will sign directly in the drawing canvas.",
          imageAlt: "signature field settings",
        },
        account: {
          title: "Account-level preference",
          text: "You can also disable typed signatures at account level if the same policy should apply to all requests.",
          note: "Use field-level control for specific templates and account-level control for organization-wide policy.",
          imageAlt: "account signature settings",
        },
        bestPractice: {
          title: "Best practice",
          text: "Add a brief note near the field so recipients know they must draw their signature.",
          tip: "Test once before sending in bulk to confirm the signer experience matches your requirements.",
        },
      },
      comingSoon: {
        body: "This guide will be added as a dedicated component page. Use the sidebar to continue browsing other resources.",
      },
    },
  },
  ar: {
    translation: {
      resourcesHome: {
        eyebrow: "كيفية الاستخدام / مركز المساعدة",
        title: "اعثر على دليل هات ساين المناسب لفريقك",
        subtitle:
          "ابحث في أدلة البدء والقوالب والتقديمات والأمان وAPI، ثم انتقل مباشرة إلى الفئة المناسبة بنقرة واحدة.",
        login: "تسجيل الدخول",
        brandLabel: "الصفحة الرئيسية لمركز مساعدة هات ساين",
        categoriesAriaLabel: "فئات مركز المساعدة",
        navHighlights: "المزايا",
        navCategories: "الفئات",
        navQuickStart: "البدء السريع",
        primaryAction: "التوثيق",
        secondaryAction: "تصفح الفئات",
        searchLabel: "ابحث في أدلة مركز المساعدة",
        searchPlaceholder:
          "ابحث في الأدلة أو القوالب أو سير العمل أو مواضيع API",
        searchSuggestionsLabel: "الأدلة المقترحة",
        searchSuggestionsEmpty: "لم يتم العثور على اقتراحات مطابقة.",
        highlightsTitle: "مصمم للوصول السريع إلى المعلومة",
        highlightsSubtitle:
          "ابدأ بأكثر الأقسام استخدامًا، ثم افتح بطاقة الفئة للوصول إلى الدليل المناسب بسرعة.",
        categoriesKicker: "قاعدة المعرفة",
        browseTitle: "تصفح حسب الفئة",
        browseSubtitle:
          "تعرض كل فئة أهم الأدلة أولًا، ويأخذك زر اقرأ المزيد إلى أول صفحة في هذا القسم داخل الشريط الجانبي.",
        searchResults:
          "تم العثور على {{count}} دليلًا مطابقًا ضمن {{sections}} فئات.",
        noSearchResults: "لم يتم العثور على أدلة مطابقة. جرّب كلمة بحث أوسع.",
        clearSearch: "مسح البحث",
        guidesCount: "{{count}} دليل",
        sectionMatches: "{{count}} نتائج",
        moreArticles: "+{{count}} مقالات إضافية",
        readMore: "اقرأ المزيد",
        ctaTitle: "احصل على الإجابة خلال دقائق واستمر في العمل.",
        ctaSubtitle:
          "ابدأ من دليل البدء السريع للوصول الأسرع، أو سجّل الدخول لمتابعة العمل داخل هات ساين.",
        ctaPrimary: "افتح البدء السريع",
        ctaSecondary: "تسجيل الدخول",
        highlightDescriptions: {
          gettingStarted:
            "ابدأ بأدلة التهيئة والأساسيات وأسرع طريقة لإرسال أول مستند.",
          templatesFields:
            "أنشئ القوالب وأدر الحقول واضبط سير التوقيع بأخطاء أقل.",
          submissions:
            "تابع حالة المستندات ونزّل المخرجات وأدر التقديمات النشطة أو المكتملة.",
          securityCompliance:
            "راجع التحكم في الوصول والتشفير والامتثال وإرشادات حماية الموقّعين.",
        },
      },
      sidebar: {
        ariaLabel: "صفحات التوثيق",
        open: "فتح الشريط الجانبي",
        close: "إغلاق الشريط الجانبي",
        noResults: "لم يتم العثور على صفحات مطابقة.",
      },
      common: {
        tipLabel: "نصيحة",
      },
      search: {
        placeholder: "ابحث...",
        open: "فتح البحث",
        clear: "مسح البحث",
        aria: "البحث في التوثيق",
        cancel: "إلغاء",
      },
      language: {
        english: "الإنجليزية",
        arabic: "العربية",
        toggleLabel: "تبديل اللغة",
      },
      pdfExport: {
        buttonLabel: "تنزيل PDF ",
        downloadAction: "تنزيل",
        preparing: "جاري التحضير...",
        coverTitle: "توثيق هات ساين",
        coverSubtitle: "دليل شامل لاستخدام تطبيق هات ساين",
        generatedAt: "تم الإنشاء في {{date}}",
        tableOfContents: "الفهرس",
        pageNumberLabel: "صفحة {{current}} من {{total}}",
        errorMessage: "تعذر إنشاء ملف PDF للتوثيق.",
      },
      pageNav: {
        previous: "السابق",
        next: "التالي",
      },
      footer: {
        poweredBy: "مشغّل بواسطة",
        haatSign: "هات ساين",
      },
      quickStart: {
        title: "البدء السريع",
        nav: {
          upload: "رفع",
          send: "إرسال",
          after: "بعد إرسال المستند",
          certificate: "شهادة التوقيع",
        },
        banner:
          "جديد على هات ساين؟ هذا الدليل يشرح إعداد المستند وتعيين المستلمين وإكمال سير توقيع آمن خطوة بخطوة.",
        startTour: "ابدأ الجولة",
        upload: {
          title: "رفع",
          steps: [
            "بعد تسجيل الدخول اضغط على زر الرفع، ويمكنك إضافة ملفات مثل PDF أو DOCX.",
            "بعد الرفع يصبح الملف قالبًا قابلًا لإعادة الاستخدام في الطلبات القادمة.",
            "استخدم المحرر لإضافة حقول التوقيع والنص والتاريخ من اللوحة الجانبية.",
            "عيّن كل حقل للمستلم المناسب حتى يعرف كل موقّع مكان الإجراء المطلوب.",
            "يمكنك إضافة أكثر من ملف داخل نفس سير العمل عبر خيار إضافة مستند.",
          ],
          note: "إعداد القالب بشكل واضح يقلل التأخير وأخطاء التوقيع.",
          imageAlt: "محرر القالب",
        },
        send: {
          title: "إرسال",
          intro:
            "بعد تجهيز القالب، أرسل المستند للمستلمين بطريقة تناسب سير عملك.",
          methods: [
            "الإرسال عبر البريد الإلكتروني أو SMS مع رابط توقيع آمن.",
            "الإرسال الجماعي عبر رفع ملف CSV أو XLSX عند الحاجة للإرسال على نطاق واسع.",
            "إنشاء رابط مشترك عندما تريد تمكين الوصول المباشر للمستند.",
          ],
          customize:
            "يمكنك تخصيص رسالة المستلم لتوضيح التعليمات قبل بدء التوقيع.",
          tip: "إذا كنت الموقّع الوحيد، استخدم خيار وقّع بنفسك لإكمال المستند فورًا.",
          roles:
            "في العمليات متعددة الأطراف، حدّد الأدوار وترتيب التوقيع لإكمال الخطوات بالتسلسل.",
          process:
            "يفتح المستلمون المستند بشكل آمن ويملؤون الحقول المطلوبة ثم يرسلون التوقيع دون إنشاء حساب.",
          observer:
            "يمكن لمستلمي CC أو BCC استلام نسخة بعد الإكمال لإبقاء أصحاب العلاقة على اطلاع.",
          notifications:
            "يمكنك ضبط من يتلقى الإشعارات ومتى يتم إرسالها من إعدادات المستلمين أو التقديم.",
          signNow:
            "إذا كنت مرسلًا وموقّعًا، يتيح لك وقّع الآن إنهاء الجزء الخاص بك قبل المتابعة للآخرين.",
          continuation:
            "بعد إنهاء دورك، يستمر سير العمل للمستلمين المتبقين وفق الترتيب المحدد.",
          note: "يمكن أن يشمل المستند الواحد عدة موقّعين ومراجعين وموافقين ضمن تدفق منظم.",
          images: {
            templateSend: "شاشة إرسال المستند",
            recipientInfo: "إعدادات المستلمين",
            recipientSubmission: "إجراءات التقديم",
          },
        },
        after: {
          title: "بعد إرسال المستند",
          p1: "تابع التقدم من لوحة التحكم لمعرفة من فتح الطلب أو أكمله ومن ما زال بانتظار التوقيع.",
          p2: "افتح القالب المرسل لرؤية حالة كل مستلم مثل: تم الإرسال، تم الفتح، قيد الانتظار، مكتمل.",
          p3: "يوفر تبويب التقديمات عرضًا شاملًا مع فلاتر وبحث حسب اسم المستند أو الموقّع.",
          note: "يتضمن سجل النشاط طوابع زمنية لمراحل التقديم والفتح والإكمال.",
          p4: "عند اكتمال توقيع جميع الأطراف المطلوبة، يتحول الطلب إلى حالة مكتمل وتتوفر الملفات النهائية.",
          p5: "للمستلمين المتأخرين يمكنك إعداد حتى ثلاثة تذكيرات تلقائية من إعدادات الحساب.",
          p6: "تبقى الملفات المكتملة متاحة من صفحة التقديمات للعرض أو التنزيل أو المشاركة.",
          tip: "المراجعة اليومية لعمليات التقديم تساعدك على المتابعة السريعة وإغلاق الطلبات في الوقت المناسب.",
          images: {
            status: "نظرة عامة على حالة المستند",
            submissions: "قائمة التقديمات",
            reminders: "إعدادات تذكيرات البريد",
          },
        },
        certificate: {
          title: "شهادة التوقيع",
          p1: "بعد اكتمال التوقيع يتم إنشاء شهادة توقيع توثق جميع تفاصيل حدث التوقيع.",
          p2: "من التقديم المكتمل يمكنك فتح شهادة التوقيع أو سجل التدقيق وتنزيله منفصلًا أو مع ملف PDF الموقّع.",
          containsTitle: "تتضمن شهادة التوقيع عادةً:",
          items: [
            "معرف المستند أو الظرف",
            "بريد الموقّعين وتواقيعهم المكتملة",
            "عناوين IP للموقّعين ومعلومات المتصفح",
            "الطوابع الزمنية لحالات التقديم والفتح والتوقيع والإكمال",
            "سجل تدقيق زمني متسلسل",
          ],
          note: "يمكن تنزيل سجل التدقيق وحده أو مع المستند الموقّع لأغراض الامتثال والأرشفة.",
          images: {
            certificate: "معاينة شهادة التوقيع",
            audit: "عرض سجل التدقيق",
          },
        },
      },
      acceptOnlyDrawn: {
        title: "قبول التوقيع المرسوم فقط",
        intro:
          "يمكن لحقل التوقيع دعم الكتابة النصية أو رفع صورة أو الرسم اليدوي. إذا كنت تحتاج توقيعًا يدويًا فقط، اجعل الحقل يقبل التوقيع المرسوم فقط.",
        howTo: {
          title: "طريقة إلزام التوقيع المرسوم",
          steps: [
            "افتح القالب وحدد حقل التوقيع.",
            "اضغط على أيقونة إعدادات الحقل لفتح الخيارات.",
            "اختر Drawn من قائمة تنسيق التوقيع.",
            "احفظ القالب ثم أرسل الطلب.",
          ],
          after:
            "بعد تفعيل هذا الإعداد، سيوقّع المستلم مباشرة داخل لوحة الرسم.",
          imageAlt: "إعدادات حقل التوقيع",
        },
        account: {
          title: "تفضيل على مستوى الحساب",
          text: "يمكنك أيضًا تعطيل التوقيع النصي من إعدادات الحساب إذا أردت تطبيق السياسة نفسها على كل الطلبات.",
          note: "استخدم إعداد الحقل عندما يخص قالبًا محددًا، وإعداد الحساب عندما تكون السياسة عامة على مستوى المؤسسة.",
          imageAlt: "إعدادات التوقيع في الحساب",
        },
        bestPractice: {
          title: "أفضل ممارسة",
          text: "أضف ملاحظة قصيرة بجوار الحقل لتوضيح أن التوقيع يجب أن يكون مرسومًا.",
          tip: "اختبر القالب مرة واحدة قبل الإرسال الجماعي للتأكد من تجربة توقيع صحيحة.",
        },
      },
      comingSoon: {
        body: "سيتم توفير هذا الدليل قريبًا كصفحة مخصصة. استخدم الشريط الجانبي لمتابعة التصفح.",
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: getSavedLanguage(),
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
